import {useMemo, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {useFormikContext} from 'formik';
import {ReactComponent as PlusIcon} from '@/assets/icons/plus.svg';
import {ReactComponent as MinusIcon} from '@/assets/icons/minus.svg';
import Button from '@/shared/components/Button';
import {CAPTION, DOLLAR, FIELD, H5, TEXT, TOTAL, ZERO} from '@/utils/constants';
import {useAppSelector} from '@/store/hooks';
import {getPrivateTablesAndTicketsTiersSelector, getTablesAndTicketsTiersSelector} from '@/store/organization-tables';
import {IOrganizationTicketsTiers} from '@/utils/types';
import {AddTableTicketFormValues} from '../../formConfiguration';
import {ticketsInfoStyle} from '../../TicketsInfo/FormContent.styles';
import Select from '@/shared/components/Select';
import FormHelperText from '@/shared/components/FormHelperText';
import {
  getCurrentAmount,
  getNewTableValues,
  getTablesCalculatedTotal,
  getTablesSubtotal,
  getUpdatedTableValues,
} from '../../TicketsInfo/helpers';
import {toast} from 'react-toastify';
import AddTicketActions from '../../AddTicketActions';
import {getUserSelector} from '@/store/user';
import Loader from '@/shared/components/Loader';
import {useApiInstance} from '@/contexts/ApiInstanceContext';
import {USER_TYPE} from '@/utils/enums';

const TicketInfoMob = () => {
  const {
    errors,
    values: {setTables},
    setFieldValue,
  } = useFormikContext<AddTableTicketFormValues>();

  const user = useAppSelector(getUserSelector);
  const {role} = useApiInstance();

  const ordinaryTicketsTiers = useAppSelector(getTablesAndTicketsTiersSelector) as IOrganizationTicketsTiers[];
  const privateTicketsTiers = useAppSelector(getPrivateTablesAndTicketsTiersSelector) as IOrganizationTicketsTiers[];

  const ticketsTiers = role === USER_TYPE.USER && privateTicketsTiers ? privateTicketsTiers : ordinaryTicketsTiers;

  const [data, setData] = useState([
    {tier: ticketsTiers?.[0].name, amount: 0, id: ticketsTiers?.[0].id, numberOfSeats: ticketsTiers?.[0].numberOfSeats},
  ]);

  const tiersOptions = useMemo(() => {
    return (
      ticketsTiers
        ?.filter((tier) => (user ? tier.isTicketsVisibleOnAuction : true))
        .map((tier) => ({id: tier.name, name: tier.name})) || []
    );
  }, [ticketsTiers, user]);

  const handleDeleteTable = (id: number) => {
    const updatedTable = getUpdatedTableValues(setTables, id);
    setFieldValue(FIELD.setTables, updatedTable);
  };

  const handleAddTable = (id: number, amount: number) => {
    const currentTier = ticketsTiers.find((t) => t.id === id);

    const maxAmount = currentTier?.maxAvailableAmount ?? 0;

    if (getCurrentAmountTables(id) >= maxAmount) {
      toast.error('You have reached the maximum amount for this tier');
      return;
    }

    const newTable = getNewTableValues(id, amount);
    setFieldValue(FIELD.setTables, [...setTables, newTable]);
  };

  const getCurrentAmountTables = (tableId: number) => getCurrentAmount(setTables, tableId);
  const getCurrentSubtotalTables = (tableId: number) => getTablesSubtotal(setTables, ticketsTiers, tableId);

  const calculateTablesTotal = useMemo(
    () => getTablesCalculatedTotal(setTables, ticketsTiers),
    [setTables, ticketsTiers]
  );

  const handleSelectChange = (index: number, value: string) => {
    const currentTier = ticketsTiers.find((t) => t.name === value);

    const isAlreadySelected = data.some((item, i) => i !== index && item.id === currentTier?.id);

    if (isAlreadySelected) {
      toast.error('This ticket tier is already selected');
      return;
    }

    if (getCurrentAmountTables(data[index].id) > 0) {
      toast.error('Remove tables before changing tier');
      return;
    }

    setData(
      data.map((item, i) =>
        index !== i
          ? item
          : {...item, tier: value, id: currentTier?.id || 0, numberOfSeats: currentTier?.numberOfSeats || 0}
      )
    );
  };

  const errorText = useMemo(() => {
    return (errors.setTables && (errors.setTables as string)) || undefined;
  }, [errors]);

  if (!ticketsTiers) return <Loader />;

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
      {data.map((item, index) => (
        <Box key={index}>
          <Select
            key={index}
            options={tiersOptions}
            label="Chose tier"
            value={item.tier}
            onChange={(e) => handleSelectChange(index, e.target.value)}
          />

          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px'}}>
            <Box>
              <Typography variant={CAPTION}>Chose tickets amount</Typography>
              <Box sx={ticketsInfoStyle.amountWrapper}>
                <Button
                  variant={TEXT}
                  startIcon={<MinusIcon />}
                  onClick={() => handleDeleteTable(item.id)}
                  disableRipple
                  disabled={getCurrentAmountTables(item.id) === ZERO}
                  sx={{
                    ...ticketsInfoStyle.deleteButton,
                    ...(getCurrentAmountTables(item.id) === ZERO && ticketsInfoStyle.disabledDeleteButton),
                  }}
                />

                <Box
                  sx={{
                    ...ticketsInfoStyle.amount,
                    ...(!item.tier && ticketsInfoStyle.disabledAmount),
                  }}
                >
                  {getCurrentAmountTables(item.id)}
                </Box>

                <Button
                  variant={TEXT}
                  endIcon={<PlusIcon />}
                  onClick={() => handleAddTable(item.id, item.numberOfSeats)}
                  disableRipple
                  sx={{...ticketsInfoStyle.addButton, ...(!item.tier && ticketsInfoStyle.disabledDeleteButton)}}
                />
              </Box>
            </Box>
            {getCurrentAmountTables(item.id) > 0 && (
              <Typography>
                {DOLLAR}
                {getCurrentSubtotalTables(item.id)}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
      {data.length !== ticketsTiers?.length && (
        <Button
          variant={TEXT}
          title="Add ticket tier"
          startIcon={<PlusIcon />}
          sx={{justifyContent: 'flex-start'}}
          onClick={() => setData([...data, {tier: '', amount: 0, id: 0, numberOfSeats: 0}])}
        />
      )}

      <Box sx={ticketsInfoStyle.footer}>
        <Typography variant={H5}>{TOTAL}</Typography>
        <Typography variant={H5}>
          {DOLLAR}
          {calculateTablesTotal}
        </Typography>
      </Box>

      {errorText && <FormHelperText error title={errorText} sx={{bottom: '140px'}} />}

      <AddTicketActions />
    </Box>
  );
};

export default TicketInfoMob;
